import type {
  FieldInput,
  InvoiceFieldDef,
  InvoiceFieldKindRule,
  InvoiceFieldKinds,
  InvoiceKindKey,
} from './invoice-field-types';

// The registry data — see invoice-field-types.ts for the model semantics.
// Registry order within each group is the document order; group order comes
// from the shared namespace (asserted in the spec).

function rule(
  kinds: Readonly<Partial<Record<InvoiceKindKey, unknown>>>,
  shared: InvoiceFieldKindRule,
): InvoiceFieldKinds {
  const out: Partial<Record<InvoiceKindKey, InvoiceFieldKindRule>> = {};
  for (const kind of Object.keys(kinds) as InvoiceKindKey[]) out[kind] = shared;
  return out;
}

const TRADE = { Provisional: {}, Final_Settlement: {} } as const;
const NOTES = { Credit_Note: {}, Debit_Note: {} } as const;
const ALL_KINDS = { Advance: {}, ...TRADE, ...NOTES } as const;
const TRADE_AND_NOTES = { ...TRADE, ...NOTES } as const;

const EDIT: InvoiceFieldKindRule = { editable: true };
const REQ: InvoiceFieldKindRule = { editable: true, required: true };

type FieldSpec = { readonly kinds: InvoiceFieldKinds } & Partial<InvoiceFieldDef>;

// A column-persisted operator field in the given document group; `spec`
// supplies the kind rules and any per-field variation (source, storage,
// docPath, formKey, …).
const grouped =
  (group: InvoiceFieldDef['group']) =>
  (key: string, label: string, input: FieldInput, spec: FieldSpec): InvoiceFieldDef => ({
    key,
    label,
    group,
    input,
    source: 'operator',
    storage: 'column',
    systemSourced: true,
    ...spec,
  });

const details = grouped('invoice_details');
const parties = grouped('parties');
const shipment = grouped('shipment');
const goods = grouped('goods');
const amounts = grouped('amounts');
const settlement = grouped('settlement');
const computed = grouped('computed_html');

// An operator-supplied override field with no upstream system value — the
// BL-body set the review dialog surfaces (the document shows an em-dash until
// the operator fills it). Editable on both sides of the trade kinds.
const blBodySpec: FieldSpec = {
  kinds: rule(TRADE, EDIT),
  storage: 'override',
  systemSourced: false,
};

export const INVOICE_FIELD_MODEL: readonly InvoiceFieldDef[] = [
  // ── Invoice details ──────────────────────────────────────────────────────
  details('invoice_number', 'Invoice Number', 'text', { kinds: rule(ALL_KINDS, REQ) }),
  details('issue_date', 'Invoice Date (Supplier)', 'date', { kinds: rule(TRADE_AND_NOTES, EDIT) }),
  details('original_due_date', 'Original Due Date', 'date', { kinds: rule(ALL_KINDS, REQ) }),
  details('remarks', 'Remarks', 'textarea', { kinds: rule(TRADE_AND_NOTES, EDIT) }),
  // Presentational on the forms: status only moves through lifecycle actions
  // (Issue, Record payment, Cancel) and is never forwarded on submit.
  details('status', 'Status', 'select', {
    kinds: rule(TRADE_AND_NOTES, { editable: false }),
    source: 'system',
    storage: 'none',
    docPath: null,
  }),
  details('contract_term_line', 'Contract term', 'text', blBodySpec),

  // ── Parties ──────────────────────────────────────────────────────────────
  parties('counterparty_name', 'Counterparty', 'company', {
    kinds: rule(ALL_KINDS, EDIT),
    formKey: 'company_name',
    source: 'contract',
  }),
  parties('counterparty_vat_id', 'Counterparty VAT ID', 'text', {
    kinds: rule(TRADE, EDIT),
    storage: 'override',
  }),
  // The supplier identity on payables — buy-side only; on receivables the
  // issuer is Metycle and is stamped from the issuer master at issue.
  parties('issuer_company_name', 'Issuer company (supplier)', 'company', {
    kinds: rule(ALL_KINDS, { editable: 'buy' }),
  }),

  // ── Shipment (operator-supplied BL-body fields) ──────────────────────────
  shipment('packing', 'Packing', 'text', blBodySpec),
  shipment('transport_mode', 'Transport mode', 'text', blBodySpec),
  shipment('vessel_voyage', 'Vessel / voyage', 'text', blBodySpec),
  shipment('eta', 'ETA', 'text', blBodySpec),

  // ── Goods & commercial terms ─────────────────────────────────────────────
  goods('description_of_goods', 'Material description', 'textarea', {
    kinds: rule(TRADE, EDIT),
    storage: 'override',
  }),
  // Assembly input for the goods description; the document renders the
  // resolved description_of_goods, so this key never binds directly.
  goods('item_description', 'Item (commodity)', 'text', {
    kinds: rule(TRADE, EDIT),
    source: 'contract',
    docPath: null,
  }),
  goods('payment_term', 'Payment Term', 'text', {
    kinds: rule(TRADE, EDIT),
    source: 'contract',
    docPath: 'formatted_payment_terms',
  }),
  goods('country_of_origin', 'Country of origin', 'text', blBodySpec),
  goods('weight_tolerance_line', 'Weight tolerance', 'text', blBodySpec),

  // ── Amounts ──────────────────────────────────────────────────────────────
  amounts('currency', 'Currency', 'select', {
    kinds: rule(ALL_KINDS, REQ),
    source: 'contract',
  }),
  amounts('amount_qt_mt', 'Quantity (MT)', 'number', {
    kinds: rule(TRADE, REQ),
    source: 'contract',
    docPath: 'loaded_quantity_mt',
  }),
  // Reads quantity × price top-down: the unit price sits above the total it
  // produces.
  amounts('market_reference_price', 'Unit Price', 'number', {
    kinds: rule(TRADE, REQ),
    source: 'contract',
  }),
  // Sell-side notes derive the total from the adjustment lines; only the buy
  // side records a hand-entered supplier total.
  amounts('total_amount', 'Total Amount', 'number', {
    kinds: {
      ...rule({ Advance: {}, ...TRADE }, REQ),
      ...rule(NOTES, { editable: 'buy', required: true }),
    },
  }),
  amounts('vat_amount', 'VAT Amount', 'number', { kinds: rule(ALL_KINDS, EDIT) }),

  // ── Settlement & note fields ─────────────────────────────────────────────
  settlement('market_reference_date', 'Market Reference Date', 'date', {
    kinds: rule(TRADE, REQ),
    source: 'contract',
  }),
  settlement('applied_downpayment_amount', 'Applied downpayment amount', 'number', {
    kinds: rule({ Advance: {}, ...TRADE }, EDIT),
    source: 'contract',
  }),
  settlement('applied_provisional_amount', 'Applied provisional amount', 'number', {
    kinds: rule({ Final_Settlement: {} }, EDIT),
    source: 'contract',
  }),
  // The tranche breakdown feeding due-date sync; assembly input only.
  settlement('payment_schedule', 'Payment schedule', 'lines', {
    kinds: rule({ Final_Settlement: {} }, EDIT),
    docPath: null,
  }),
  // The parent-invoice relation; the document renders the resolved parent
  // number, the form persists the FK.
  settlement('adjusts_invoice_id', 'Reference invoice', 'select', {
    kinds: rule(NOTES, REQ),
    docPath: 'adjusts_invoice_number',
  }),
  settlement('note_reason', 'Reason', 'select', { kinds: rule(NOTES, EDIT) }),
  settlement('settlement_method', 'Settlement method', 'select', { kinds: rule(NOTES, EDIT) }),
  // Debit-only free text, folded into remarks on the wire.
  settlement('payment_terms', 'Payment terms', 'text', {
    kinds: rule({ Debit_Note: {} }, EDIT),
    storage: 'none',
    docPath: null,
  }),
  // The declared price/weight change behind a note: form-only inputs — the
  // wire carries them as value_change and the amount is recomputed from the
  // delta server-side; the document renders the resulting figures, never these.
  settlement('change_driver', 'What changed', 'select', {
    kinds: rule(NOTES, EDIT),
    storage: 'none',
    docPath: null,
  }),
  settlement('change_new_value', 'New value', 'number', {
    kinds: rule(NOTES, EDIT),
    storage: 'none',
    docPath: null,
  }),

  // ── Computed blocks ──────────────────────────────────────────────────────
  // Tax-line wording; the advance template binds it today, the other kinds'
  // wiring is a finance/legal follow-up (mirrors the shared allow-set note).
  computed('vat_note_html', 'VAT note', 'textarea', {
    kinds: rule({ Advance: {} }, EDIT),
    storage: 'override',
  }),

  // ── Adjustment lines ─────────────────────────────────────────────────────
  grouped('adjustment_lines')('adjustment_lines', 'Adjustment lines', 'lines', {
    kinds: rule(NOTES, { editable: 'sell' }),
  }),

  // ── Constrained relations (not free fields) ──────────────────────────────
  // The issuing bank: an FK validated against the entity catalog, selected
  // at review on receivables. Feeds the rendered payment-details block.
  computed('issuer_bank_id', 'Issuing bank', 'select', {
    kinds: rule(TRADE, { editable: 'sell' }),
    storage: 'relation',
    docPath: 'payment_details_block',
  }),
  // Receiving-account routing for the payment block; 'auto' keeps the
  // backend's currency/country routing.
  computed('payment_account_key', 'Receiving bank account', 'select', {
    kinds: rule(TRADE, { editable: 'sell' }),
    docPath: 'payment_details_block',
  }),
];
